import { useCallback, useEffect, useMemo } from 'react';

import { useGetAlbumVideosLazy } from '@vimeo/gctl-api/hooks/useAlbumVideos';
import { type Video } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { useViewer } from '@vimeo/viewer';
import { useBaseUrl } from '@vimeo/viewer-showcase-ui/hooks/use-base-url';
import { getEventIdFromClip } from '@vimeo/viewer-showcase-ui/utils/helper';

import { useAlbumStore } from '../state/albumStore';
import { SELECT_ALBUM_FIELDS, VIDEOS_API_VERSION } from '../utils/constants';
import { contextUrlParser, findWithAdjacent } from '../utils/helper';
import { useGetPaginatedVideos } from './use-get-playlist-videos';
import { useGetShowcase } from './use-get-showcase';
import { useSelectedId } from './use-selected-id';

const DEFAULT_SHOWCASE_QUERY = {
  sizes: ['640x360', '360x203'],
  forceCollectionUri: true,
};

/**
 * Custom hook to fetch adjacent playlist videos and prefetch next/previous pages when needed.
 */
export const useGetAdjacentPlaylistVideo = () => {
  const [getAlbumVideoData, state] = useGetAlbumVideosLazy();
  const viewer = useViewer();
  const baseUrl = useBaseUrl();
  const { selectedId } = useSelectedId();
  const { clips, total, perPage, page: curPage } = useGetPaginatedVideos();
  const { sortConfig, hideUpcoming } = useGetShowcase();
  const {
    albumId,
    password,
    isShowcaseEmbedded: isEmbed,
    referer,
  } = useAlbumStore((state) => ({
    albumId: state.albumId,
    password: state.hashedPass,
    isShowcaseEmbedded: state.isShowcaseEmbedded,
    referer: state.referrer,
  }));

  const { prev, next } = findWithAdjacent(clips ?? [], (clip) => clip.clipId === selectedId);
  const totalPages = useMemo(() => Math.ceil((total || 0) / (perPage || 1)), [total, perPage]);

  // Prefetch next page if the current clip is the last clip on the current page and there are more pages
  const prefetchNextPage =
    curPage !== totalPages && selectedId === clips?.[clips.length - 1]?.clipId;
  // Prefetch previous page if the current clip is the first clip on the current page and there are previous pages
  const prefetchPreviousPage = curPage !== 1 && selectedId === clips?.[0]?.clipId;

  const loadAdjacentPage = useCallback(
    (direction: 'prev' | 'next') => {
      if (!viewer || !curPage) return;

      const page = direction === 'prev' ? curPage - 1 : curPage + 1;

      getAlbumVideoData({
        where: { albumId },
        select: SELECT_ALBUM_FIELDS,
        query: {
          isEmbed,
          page,
          perPage,
          ...DEFAULT_SHOWCASE_QUERY,
          ...sortConfig,
          ...(password && { password }),
          ...(hideUpcoming && { filter: 'playable' }),
          referrer: referer,
        },
        headers: { Accept: `application/vnd.vimeo.*+json;version=${VIDEOS_API_VERSION}` },
      });
    },
    [
      albumId,
      curPage,
      hideUpcoming,
      isEmbed,
      password,
      perPage,
      sortConfig,
      viewer,
      getAlbumVideoData,
    ],
  );

  const getAdjacentClipData = (videos: Video[], direction: 'prev' | 'next') => {
    const index = direction === 'next' ? 0 : videos.length - 1;
    const video = videos?.[index];
    const eventId = getEventIdFromClip(video);
    return {
      ...video,
      ...contextUrlParser(video?.link, { baseUrl, eventId }),
      ...(eventId && { eventId }),
    };
  };

  /**
   * Loads the next or previous page of videos when current page is at the boundaries of the current page.
   */
  useEffect(() => {
    if (prefetchNextPage) {
      loadAdjacentPage('next');
    } else if (prefetchPreviousPage) {
      loadAdjacentPage('prev');
    }
  }, [loadAdjacentPage, prefetchNextPage, prefetchPreviousPage]);

  /**
   * Determine if the next or previous video data should be fetched, if not use the existing data.
   */
  const videos = state?.data?.data;
  const hasFetchedNextPage = prefetchNextPage && videos && !next;
  const hasFetchedPreviousPage = prefetchPreviousPage && videos && !prev;
  const nextClipData = hasFetchedNextPage ? getAdjacentClipData(videos, 'next') : next;
  const prevClipData = hasFetchedPreviousPage ? getAdjacentClipData(videos, 'prev') : prev;

  return {
    nextClipData,
    prevClipData,
    selectedId,
    clipIndex: clips?.findIndex((clip) => clip.clipId === selectedId),
  };
};
